import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../../lib/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowLeft, GamepadIcon, Clock, Users, MapPin, Upload, X } from 'lucide-react';
import { uploadToR2 } from '../../lib/r2Upload';
import { ImagePreview } from '../../components/ImagePreview';

export default function AdminGames() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    shortDescription: '',
    story: '',
    imageUrl: '',
    videoUrl: '',
    images: [],
    duration: '',
    playerCount: '',
    price: '',
    showPrice: true,
    branches: []
  });

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    // Real-time listener for games
    const unsubscribeGames = onSnapshot(
      collection(db, 'games'),
      (snapshot) => {
        const gamesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setGames(gamesData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching games:', error);
        setLoading(false);
      }
    );

    // Real-time listener for branches
    const unsubscribeBranches = onSnapshot(
      collection(db, 'branches'),
      (snapshot) => {
        const branchesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setBranches(branchesData);
      },
      (error) => {
        console.error('Error fetching branches:', error);
      }
    );

    return () => {
      unsubscribeGames();
      unsubscribeBranches();
    };
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      shortDescription: '',
      story: '',
      imageUrl: '',
      videoUrl: '',
      images: [],
      duration: '',
      playerCount: '',
      price: '',
      showPrice: true,
      branches: []
    });
    setEditingGame(null);
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    setFormData({
      name: game.name || '',
      shortDescription: game.shortDescription || '',
      story: game.story || '',
      imageUrl: game.imageUrl || '',
      videoUrl: game.videoUrl || '',
      images: game.images || [],
      duration: game.duration?.toString() || '',
      playerCount: game.playerCount || '',
      price: game.price?.toString() || '',
      showPrice: game.showPrice !== false,
      branches: game.branches || []
    });
    setDialogOpen(true);
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadToR2(file, 'games');
      
      if (result.success) {
        setFormData({ ...formData, imageUrl: result.url });
        toast.success('Şəkil yükləndi');
      } else {
        toast.error(`Yükləmə xətası: ${result.error}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Şəkil yükləmə xətası');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const result = await uploadToR2(file, 'games');
        
        if (result.success) {
          uploadedUrls.push(result.url);
        } else {
          toast.error(`Yükləmə xətası: ${result.error}`);
        }
      }

      if (uploadedUrls.length > 0) {
        setFormData({
          ...formData,
          images: [...formData.images, ...uploadedUrls]
        });
        toast.success(`${uploadedUrls.length} şəkil yükləndi`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Şəkil yükləmə xətası');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveGalleryImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) return; // Prevent double submit

    if (!formData.name) {
      toast.error('Oyun adı tələb olunur');
      return;
    }

    setSubmitting(true);
    try {
      const gameData = {
        name: formData.name,
        shortDescription: formData.shortDescription,
        story: formData.story,
        imageUrl: formData.imageUrl,
        videoUrl: formData.videoUrl,
        images: formData.images,
        duration: formData.duration ? parseInt(formData.duration) : null,
        playerCount: formData.playerCount,
        price: formData.showPrice && formData.price ? parseFloat(formData.price) : null,
        showPrice: formData.showPrice,
        branches: formData.branches,
        updatedAt: new Date().toISOString()
      };

      if (editingGame) {
        await updateDoc(doc(db, 'games', editingGame.id), gameData);
        toast.success('Oyun yeniləndi');
      } else {
        gameData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'games'), gameData);
        toast.success('Oyun əlavə edildi');
      }

      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving game:', error);
      toast.error('Xəta baş verdi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (gameId) => {
    if (!window.confirm('Bu oyunu silmək istədiyinizə əminsiniz?')) return;

    try {
      await deleteDoc(doc(db, 'games', gameId));
      toast.success('Oyun silindi');
    } catch (error) {
      console.error('Error deleting game:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        toast.error('Silmə icazəsi yoxdur. Firebase rules-u yoxlayın.');
      } else {
        toast.error(`Xəta: ${error.message}`);
      }
    }
  };

  const toggleBranch = (branchId) => {
    setFormData(prev => ({
      ...prev,
      branches: prev.branches.includes(branchId)
        ? prev.branches.filter(id => id !== branchId)
        : [...prev.branches, branchId]
    }));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#D90429] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050000] pt-20" data-testid="admin-games-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-cinzel text-2xl md:text-3xl font-bold text-white">
                Oyunlar
              </h1>
              <p className="text-gray-400 text-sm">{games.length} oyun</p>
            </div>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#D90429] hover:bg-[#B00220]" data-testid="add-game-btn">
                <Plus className="w-5 h-5 mr-2" />
                Yeni Oyun
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F0F0F] border-[#333] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-cinzel text-xl">
                  {editingGame ? 'Oyunu Redaktə Et' : 'Yeni Oyun Əlavə Et'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-gray-400">Oyun Adı *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="Psychiatric Ward 13"
                      data-testid="game-name-input"
                    />
                  </div>

                  <div className="col-span-2">
                    <Label className="text-gray-400">Qısa Təsvir</Label>
                    <Input
                      value={formData.shortDescription}
                      onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="Qısa təsvir..."
                    />
                  </div>

                  <div className="col-span-2">
                    <Label className="text-gray-400">Hekayə</Label>
                    <Textarea
                      value={formData.story}
                      onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1 min-h-32"
                      placeholder="Oyunun hekayəsi..."
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400">Müddət (dəqiqə)</Label>
                    <Input
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="60"
                      data-testid="game-duration-input"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400">Oyunçu Sayı</Label>
                    <Input
                      value={formData.playerCount}
                      onChange={(e) => setFormData({ ...formData, playerCount: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="2-6"
                      data-testid="game-players-input"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400">Qiymət (AZN)</Label>
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="40"
                      disabled={!formData.showPrice}
                      data-testid="game-price-input"
                    />
                  </div>

                  <div className="flex items-center gap-2 self-end pb-2">
                    <Switch
                      checked={formData.showPrice}
                      onCheckedChange={(checked) => setFormData({ ...formData, showPrice: checked })}
                    />
                    <Label className="text-gray-400">Qiymət göstər</Label>
                  </div>

                  {/* Main Image - URL only + zoom preview */}
                  <div className="col-span-2">
                    <Label className="text-gray-400">Əsas Şəkil URL</Label>
                    <div className="flex gap-3 mt-1 items-start">
                      <Input
                        value={formData.imageUrl}
                        onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                        className="bg-[#0A0A0A] border-white/10 text-white flex-1"
                        placeholder="https://... və ya /oyunlar/xxx.jpeg"
                        data-testid="game-image-url"
                      />
                      {formData.imageUrl && (
                        <ImagePreview url={formData.imageUrl} alt="Əsas şəkil" size="md" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Şəkil URL-ini yapışdırın. Böyütmək üçün preview-a klik edin.</p>
                  </div>

                  <div className="col-span-2">
                    <Label className="text-gray-400">Video URL</Label>
                    <Input
                      value={formData.videoUrl}
                      onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="https://..."
                    />
                  </div>

                  {/* Gallery Images - URLs only */}
                  <div className="col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-gray-400">Qalereya URL-ləri</Label>
                      <span className="text-xs text-gray-500">{formData.images.length} şəkil</span>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        id="gallery-url-input"
                        placeholder="https://... URL daxil edin və Əlavə et düyməsinə basın"
                        className="bg-[#0A0A0A] border-white/10 text-white flex-1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const v = e.target.value.trim();
                            if (v) {
                              setFormData({ ...formData, images: [...formData.images, v] });
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="border-white/20 text-white"
                        onClick={() => {
                          const input = document.getElementById('gallery-url-input');
                          const v = input?.value.trim();
                          if (v) {
                            setFormData({ ...formData, images: [...formData.images, v] });
                            input.value = '';
                          }
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Əlavə et
                      </Button>
                    </div>

                    {formData.images.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 mt-3">
                        {formData.images.map((url, index) => (
                          <div key={index} className="relative aspect-square group">
                            <ImagePreview url={url} alt={`Gallery ${index + 1}`} size="lg" className="w-full h-full" />
                            <button
                              type="button"
                              onClick={() => handleRemoveGalleryImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 z-10"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="col-span-2">
                    <Label className="text-gray-400">Filiallar</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {branches.map((branch) => (
                        <Button
                          key={branch.id}
                          type="button"
                          variant={formData.branches.includes(branch.id) ? 'default' : 'outline'}
                          className={formData.branches.includes(branch.id)
                            ? 'bg-[#D90429] hover:bg-[#B00220]'
                            : 'border-white/20 text-gray-400 hover:bg-white/10'
                          }
                          onClick={() => toggleBranch(branch.id)}
                        >
                          <MapPin className="w-4 h-4 mr-1" />
                          {branch.name}
                        </Button>
                      ))}
                      {branches.length === 0 && (
                        <p className="text-gray-500 text-sm">Əvvəlcə filial əlavə edin</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 border-white/20"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    disabled={submitting}
                  >
                    Ləğv Et
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#D90429] hover:bg-[#B00220]"
                    data-testid="save-game-btn"
                    disabled={submitting}
                  >
                    {submitting ? 'Yüklənir...' : (editingGame ? 'Yenilə' : 'Əlavə Et')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Games List */}
        {games.length === 0 ? (
          <div className="text-center py-16">
            <GamepadIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">Hələ oyun yoxdur</h3>
            <p className="text-gray-400">İlk oyununuzu əlavə edin</p>
          </div>
        ) : (
          <div className="space-y-4">
            {games.map((game, index) => (
              <motion.div
                key={game.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#0F0F0F] border border-white/10 rounded-lg overflow-hidden hover:border-[#D90429]/30 transition-colors"
                data-testid={`admin-game-${game.id}`}
              >
                <div className="flex flex-col md:flex-row">
                  {/* Image */}
                  <div className="md:w-48 h-32 md:h-auto bg-[#0A0A0A]">
                    {game.imageUrl ? (
                      <img
                        src={game.imageUrl}
                        alt={game.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <GamepadIcon className="w-12 h-12 text-gray-700" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 p-4 md:p-6">
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                      <div className="flex-1">
                        <h3 className="font-cinzel text-lg sm:text-xl font-bold text-white mb-2">
                          {game.name}
                        </h3>
                        <div className="flex flex-wrap gap-3 sm:gap-4 text-gray-400 text-sm">
                          {game.duration && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {game.duration} dəq
                            </span>
                          )}
                          {game.playerCount && (
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {game.playerCount} nəfər
                            </span>
                          )}
                          {game.price && (
                            <span className="text-[#D90429] font-semibold">
                              {game.price} AZN
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2 self-start">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(game)}
                          className="text-gray-400 hover:text-white hover:bg-white/10"
                          data-testid={`edit-game-${game.id}`}
                        >
                          <Pencil className="w-5 h-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(game.id)}
                          className="text-gray-400 hover:text-[#D90429] hover:bg-[#D90429]/10"
                          data-testid={`delete-game-${game.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>

                    {game.branches && game.branches.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {game.branches.map((branchId) => {
                          const branch = branches.find(b => b.id === branchId);
                          return branch ? (
                            <span
                              key={branchId}
                              className="text-xs bg-[#D90429]/20 text-[#D90429] px-2 py-1 rounded"
                            >
                              {branch.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
